<template>
  <div class="status-row">
    <template v-if="!deleted">
      <div
        class="status"
        v-if="!editMode"
        :class="{ updating: isUpdating }"
        @mousemove="hideSavedMessage"
      >
        <trigger
          class="status-switch"
          v-model="onTop"
          v-tooltip="{
            content: $tr('settings.statuses.maxStatuses'),
            placement: 'top-center',
            classes: [triggerClasses, 'width140']
          }"
          :disabled="disableTriggerOnTop"
        />

        <span class="name">{{ name }}</span>

        <div class="right-part">
          <transition name="fade">
            <div class="saved" v-show="saved && !isUpdating">
              <i class="icon icon-done"></i>
              <span>{{ $tr('settings.statuses.saved') }}</span>
            </div>
          </transition>

          <div
            v-if="model.is_base || disabledStatus"
            class="lock"
            v-tooltip="{
              content: $tr('settings.statuses.baseStatus'),
              placement: 'top-center',
              classes: 'width160'
            }"
          >
            <div class="icon-lock"></div>
          </div>

          <div v-else class="actions">
            <vbutton
              class="group"
              color="transparent"
              type="icon"
              icon="icon_edit"
              :loading="isUpdating"
              @click="toggleEditMode"
            />

            <vbutton
              class="group"
              color="transparent"
              type="icon"
              icon="icon-bin"
              :disabled="model.has_responses"
              v-tooltip="{
                content: $tr('settings.statuses.statusWithCandidates'),
                placement: 'top-center',
                classes: [canNotDelete, 'width160']
              }"
              :loading="isUpdating"
              @click="deleteThisStatus"
            />
          </div>
        </div>
      </div>

      <edit-row
        class="edit-row"
        v-else
        :initialName="name"
        :callback-with-name="editName"
        @close="toggleEditMode"
      />
    </template>

    <deleted-row
      v-else
      :status="model"
      @undoFinallyDelete="undoFinallyDelete"
      @finallyDelete="finallyDelete"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Trigger from '@/components/shared/MinimalisticTrigger.vue';
import EditRow from './AddRow.vue';
import DeletedRow from './DeletedRow.vue';
import PROCESS_NAMES from '@/process/names';

const DELETED_TIMEOUT_MS = 20 * 1000;
const SHOW_SAVED_LABEL = 5 * 1000;

export default {
  name: 'settings-status',

  components: {
    Trigger,
    EditRow,
    DeletedRow
  },

  props: {
    index: Number,
    model: Object,
    haveSpaceInTheStatusBar: Boolean
  },

  watch: {
    isUpdating(val) {
      if (!val) {
        this.saved = true;
        clearTimeout(this.timeoutSaved);
        this.timeoutSaved = setTimeout(() => {
          this.saved = false;
        }, SHOW_SAVED_LABEL);
      }
    },

    deleted(val) {
      if (val) {
        clearTimeout(this.timeoutSaved);
        this.saved = false;

        this.finallyDeleteTimeout = setTimeout(() => {
          this.$store.dispatch('dictionary/finallyDelete', {
            id: this.model.id
          });
        }, DELETED_TIMEOUT_MS);
      }
    }
  },

  methods: {
    editName(name) {
      this.updateModel({ name: name });
    },

    updateModel(data) {
      this.$store
        .dispatch(
          'dictionary/updateResponseStatus',
          Object.assign(data, {
            id: this.model.id
          })
        )
        .catch(e => {
          this.$noty.error(e.data.meta.error.message);
        });
    },

    deleteThisStatus() {
      if (!this.model.has_responses) {
        this.$store.dispatch('dictionary/deleteResponseStatuses', {
          id: this.model.id
        });
      }
    },

    undoFinallyDelete() {
      clearTimeout(this.finallyDeleteTimeout);
      this.$store.dispatch('dictionary/undoFinallyDelete', {
        id: this.model.id
      });
    },

    finallyDelete() {
      clearTimeout(this.finallyDeleteTimeout);
      this.$store.dispatch('dictionary/finallyDelete', {
        id: this.model.id
      });
    },

    toggleEditMode() {
      this.editMode = !this.editMode;
    },

    hideSavedMessage() {
      clearTimeout(this.timeoutSaved);
      this.saved = false;
    }
  },

  computed: {
    ...mapGetters('process', ['getProcessName']),

    // techlab
    disabledStatus() {
      if (
        this.getProcessName === PROCESS_NAMES.MARS ||
        this.getProcessName === PROCESS_NAMES.MARS_MIEP ||
        this.getProcessName === PROCESS_NAMES.MARS_MLEP
      ) {
        if (
          Number(this.model.id) === 55896 ||
          Number(this.model.id) === 55897 ||
          Number(this.model.id) === 60498
        ) {
          return true;
        }
      }

      return false;
    },
    // end of techlab

    disableTriggerOnTop() {
      // If active, disable when too few statuses in a status bar
      // If innactive, disable when too much statuses in a status bar
      return this.model.on_top ? false : !this.haveSpaceInTheStatusBar;
    },

    triggerClasses() {
      return this.disableTriggerOnTop ? '' : 'hide';
    },

    canNotDelete() {
      return this.model.has_responses ? '' : 'hide';
    },

    isUpdating() {
      return this.updatingStatusesLocalIds.includes(this.model.localId);
    },

    updatingStatusesLocalIds() {
      return this.$store.getters['dictionary/updatingStatusesLocalIds'];
    },

    deleted() {
      return this.model.deleted;
    },

    onTop: {
      get() {
        return this.model.on_top;
      },

      set(val) {
        this.updateModel({ on_top: val });
      }
    },

    name: {
      get() {
        return this.model.name;
      },

      set(val) {
        this.updateModel({ name: val });
      }
    }
  },

  data() {
    return {
      editMode: false,
      saved: false,
      finallyDeleteTimeout: null
    };
  },

  beforeDestroy() {
    clearTimeout(this.timeoutSaved);

    // Don`t forget to delete from vuex temporary saved,
    // but deleted from back, status
    if (this.finallyDeleteTimeout) {
      clearTimeout(this.finallyDeleteTimeout);
      this.$store.dispatch('dictionary/finallyDelete', {
        id: this.model.id
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

@keyframes spinner {
  to {
    transform: rotate(360deg);
  }
}

.status-row {
  display: flex;
  align-items: center;
  width: 100%;

  &:first-child .status {
    border-top: 1px solid #dddddd;
  }

  &:not(.not-draggable) .status {
    cursor: all-scroll;
  }
}

.status {
  display: flex;
  align-items: center;
  padding: 20px 20px 20px 10px;
  box-sizing: border-box;
  width: 50%;
  min-width: 500px;
  border-bottom: 1px solid #dddddd;
  position: relative;

  &:hover {
    .lock,
    .actions {
      display: flex;
    }
  }

  &.updating {
    pointer-events: none;

    > *:not(.saving) {
      opacity: 0.3;
    }
  }

  .status-switch {
    margin-right: 25px;
  }

  .name {
    font-size: 16px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 5px;
  }

  .right-part {
    margin-left: auto;
    display: flex;
    align-items: center;
  }

  .lock,
  .actions {
    display: flex;
    align-items: center;
  }

  .lock {
    width: 40px;
    text-align: center;
    display: flex;
    justify-content: center;
    box-sizing: border-box;
    &:hover {
      cursor: default;
    }

    .icon-lock {
      width: 14px;
      height: 18px;
      background: url(/static/images/elements/lock.svg) center no-repeat;
      background-size: contain;
    }

    span {
      color: #7f7f7f;
      font-size: 16px;
      font-weight: 500;
    }
  }

  .actions {
    .group + .group {
      margin-left: 22px;
    }

    .group {
      display: flex;
      align-items: center;

      &:hover {
        cursor: pointer;
      }

      &.disable {
        cursor: not-allowed;
      }
    }

    .icon {
      color: $green;
      margin-right: 7.5px;
    }

    span {
      color: $green;
      font-size: 16px;
      font-weight: 500;
    }
  }

  .saving {
    width: 18px;
    height: 18px;
    position: absolute;
    right: 20px;

    &:before {
      content: '';
      box-sizing: border-box;
      position: absolute;
      top: 50%;
      left: 50%;
      width: 18px;
      height: 18px;
      margin-top: -9px;
      margin-left: -9px;
      border-radius: 50%;
      border: 3px solid transparent;
      border-top-color: #7f7f7f;
      border-left-color: #7f7f7f;
      border-bottom-color: #7f7f7f;
      animation: spinner 0.6s linear infinite;
    }
  }

  .saved {
    display: flex;
    align-items: center;

    .icon-done {
      color: #7f7f7f;
      font-size: 10px;
      margin-right: 3px;
    }

    + .actions,
    + .lock {
      margin-left: 10px;
    }
  }
}

.edit-row {
  border-bottom: 1px solid #dddddd;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
